import { assert } from '@disy/lib/custom-error';

import type { Gradient, GradientGroup, GradientId } from 'ui/gradient-picker/gradient';

import { getConfiguration } from 'cadenza/config/configuration';
import type { Color, ColorPaletteDto, RgbaColor } from 'cadenza/utils/colors/colors';
import { interpolateRgbColor, parseColor } from 'cadenza/utils/colors/colors';

declare module 'cadenza/config/configuration' {
  interface Configuration {
    color: ColorConfigurationDto;
  }
}

/** @see net/disy/cadenza/workbook/web/dto/ColorConfigurationDto.java */
export interface ColorConfigurationDto {
  colorPalettes: string[];
  colorPalettesById: Record<string, ColorPaletteDto>;
  extendedColorsById: Record<string, RgbaColor[]>;
  textColorPalette?: string;
  backgroundColorPalette?: string;
  gradientsById: Record<GradientId, GradientDto>;
  gradientGroups: GradientGroupDto[];
  heatmapGradientGroups: GradientGroupDto[];
  defaultBulletChartGradient: string;
}

/** @see net/disy/cadenza/workbook/web/dto/GradientDto.java */
interface GradientDto {
  id: GradientId;
  title: string;
  colorPaletteId: string;
}

/** @see net/disy/cadenza/workbook/web/dto/GradientGroupDto.java */
interface GradientGroupDto {
  title: string;
  gradients: GradientId[];
}

export function getColorConfiguration() {
  return getConfiguration('color');
}

export function getColorPaletteById(colorPaletteId: string) {
  return getColorConfiguration().colorPalettesById[colorPaletteId];
}

export function isValidColorPaletteId(colorPaletteId: string) {
  return getColorConfiguration().colorPalettesById[colorPaletteId] !== undefined;
}

export function getExtendedColorsById(colorPaletteId: string) {
  return getColorConfiguration().extendedColorsById[colorPaletteId];
}

export function getDefaultWorkbookPalette() {
  return getColorPaletteById(getColorConfiguration().colorPalettes[0]);
}

export function getTextColorPalette() {
  return getColorPaletteById(getColorConfiguration().textColorPalette!).colors;
}

export function getBackgroundColorPalette() {
  return getColorPaletteById(getColorConfiguration().backgroundColorPalette!).colors;
}

export function getColorPalettes() {
  return getColorConfiguration().colorPalettes.map((colorPalette) => getColorPaletteById(colorPalette));
}

export function isValidGradientId(gradientId: GradientId) {
  return getColorConfiguration().gradientsById[gradientId] !== undefined;
}

export function getGradientById(gradientId: GradientId): Gradient {
  const { id, title, colorPaletteId } = getColorConfiguration().gradientsById[gradientId];
  const { colors } = getColorPaletteById(colorPaletteId);
  return { id, title, colors };
}

export function getWorkbookGradientGroups(): GradientGroup[] {
  return getColorConfiguration().gradientGroups.map(({ title, gradients }) => ({
    title,
    gradients: gradients.map((gradientId) => getGradientById(gradientId)),
  }));
}

export function getHeatmapGradientGroups(): GradientGroup[] {
  return getColorConfiguration().heatmapGradientGroups.map(({ title, gradients }) => ({
    title,
    gradients: gradients.map((gradientId) => getGradientById(gradientId)),
  }));
}

export function getGradientColors(numberOfClasses: number, gradientId: GradientId, reversedGradient = false) {
  if (numberOfClasses === 0) {
    return [];
  }
  const { colors } = getGradientById(gradientId);
  const gradientColorsToUse = reversedGradient ? [...colors].reverse() : colors;
  // for only one class use middle gradient color
  if (numberOfClasses < 2) {
    return [parseColor(gradientColorsToUse[Math.round((gradientColorsToUse.length - 1) / 2)])];
  }
  const colorsFromGradient = [];
  const increment = 1 / (numberOfClasses - 1);

  for (let colorIndex = 0; colorIndex < numberOfClasses; colorIndex++) {
    const fraction = increment * colorIndex;
    colorsFromGradient.push(getInterpolatedGradientColorAt(gradientColorsToUse, fraction));
  }
  return colorsFromGradient;
}

/** @see net/disy/commons/core/util/ColorUtilities.java */
function getInterpolatedGradientColorAt(gradientColors: Color[], fraction: number) {
  assert(fraction >= 0 && fraction <= 1.0, 'Fraction must be between 0 and 1');
  if (!gradientColors.length) {
    return parseColor('#000000');
  }
  if (gradientColors.length === 1) {
    return parseColor(gradientColors[0]);
  }
  if (fraction === 0) {
    return parseColor(gradientColors[0]);
  }
  if (fraction === 1) {
    // !: we should have other colors as previous conditions checks for it
    return parseColor(gradientColors.at(-1)!);
  }
  const step = 1.0 / (gradientColors.length - 1);
  let endIndex = 1;
  while (step * endIndex <= fraction) {
    endIndex++;
  }
  const normalizedFraction = (fraction % step) / step;
  const startColor = parseColor(gradientColors[endIndex - 1]);
  const endColor = parseColor(gradientColors[endIndex]);
  return parseColor(interpolateRgbColor(startColor, endColor, normalizedFraction));
}
